import React from 'react';
import { TableInstance } from 'react-table';

import { useTablePrefilterContext } from '@work4all/components/lib/components/table/TablePrefilterProvider';

import { nameof } from '@work4all/data/lib/helper/nameof';

import { Appointment } from '@work4all/models/lib/Classes/Appointment.entity';
import { SortDirection } from '@work4all/models/lib/DataProvider';
import { EMailTemplateKind } from '@work4all/models/lib/Enums/EMailTemplateKind.enum';
import { EMode } from '@work4all/models/lib/Enums/EMode.enum';

import { EntityTable, IEntityTable } from '../entity-table/EntityTable';
import { useEntityDataTable } from '../entity-table/use-entity-data-table';
import schema from '../schemata/appointment-table-schema.json';

import { useAppointmentEmailTemplate } from './hooks/use-appointment-email-template';
import { useFixedAppointmentsData } from './hooks/use-fixed-appointments-data';
import { useGetAppoinmentEmailsParams } from './hooks/use-get-appoinment-emails-params';

const APPOINTMENT_FORCE_REQUEST_FIELDS: Appointment<EMode.query> = {
  fromAbsolute: null,
  toAbsolute: null,
  appointmentAttendeeList: [
    {
      id: null,
      ressource: {
        id: null,
        eMail: null,
        displayName: null,
        loginName: null,
      },
      user: {
        id: null,
        eMail: null,
        displayName: null,
        loginName: null,
      },
      contact: {
        id: null,
        eMail: null,
        displayName: null,
      },
      businessPartner: {
        id: null,
        data: {
          customer: { id: null, eMail: null },
          supplier: { id: null, eMail: null },
        },
      },
    },
  ],
  contact: {
    id: null,
    displayName: null,
    eMail: null,
  },
  startDate: null,
  endDate: null,
  isWholeDay: null,
  city: null,
  title: null,
  note: null,
  contract: {
    id: null,
    contractNumber: null,
  },
  deliveryNote: {
    id: null,
    number: null,
  },
};

const defaultSort = [
  {
    field: nameof<Appointment>('startDate'),
    direction: SortDirection.DESCENDING,
  },
];

export const AppointmentsTable = React.forwardRef<TableInstance, IEntityTable>(
  function AppointmentsTable(_props, ref) {
    const { prefilter } = useTablePrefilterContext();

    const dataTable = useEntityDataTable<Appointment, never>({
      schema: schema as never,
      prefilter,
      defaultSort,
      forceRequestFields: APPOINTMENT_FORCE_REQUEST_FIELDS,
    });

    // Fix appointment start and end dates for full day appointments.
    const data = useFixedAppointmentsData(dataTable.data);

    const getEmailParams = useGetAppoinmentEmailsParams(
      dataTable.selectedEntity
    );

    const { action, component } = useAppointmentEmailTemplate({
      getEmailParams,
      eMailTemplateKind: EMailTemplateKind.APPOINTMENT_NOTIFICATION,
      noTemplate: true,
      appointments: dataTable.selectedEntities,
    });

    return (
      <React.Fragment>
        {component}
        <EntityTable
          ref={ref}
          {...dataTable}
          {..._props}
          data={data}
          actions={{
            custom: {
              right: [action],
            },
          }}
        />
      </React.Fragment>
    );
  }
);
